// @tennis-matches/services/auth
// Web Auth facade (Supabase Auth v2)
// Migrated from src/services/auth/auth.service.js

import {
  getSupabaseClient,
  getSupabaseClientSync,
  getSupabaseUser,
  readSupabaseClientConfig,
  resetSupabaseClientForAuthRedirect,
  subscribeSupabaseAuthStateChange,
  syncSupabaseAuthBearerFromSession,
  ttbRunSerializedAuthGetSession,
} from "../supabase/index.js";
import { isTrainingsDeskInvitePathname } from "@tennis-matches/core";
import {
  beginAuthDebugBootstrap,
  setAuthDebugBootstrapDeadline,
  setAuthDebugFailure,
  setAuthDebugStep,
} from "./auth-debug-state.js";

/** Produktions-App immer per HTTPS (Supabase Redirect-Allowlist). */
export function resolveAuthRedirectOrigin() {
  try {
    if (typeof window === "undefined" || !window.location?.origin) return "";
    const origin = String(window.location.origin || "").trim();
    if (/^http:\/\/(app\.)?tennis-matches\.de$/i.test(origin)) {
      return "https://app.tennis-matches.de";
    }
    if (/^http:\/\/(www\.)?tennis-matches\.de$/i.test(origin)) {
      return "https://www.tennis-matches.de";
    }
    return origin;
  } catch {
    return "";
  }
}

/**
 * Kanonische Redirect-URL für OAuth (`signInWithOAuth`) und E-Mail-Bestätigung (`signUp`):
 * immer `${origin}/auth/callback`, optional `?next=` für relatives Ziel.
 * @param {string} [nextPath] z. B. `/feature-selection` — nur interne Pfade mit führendem `/`
 */
export function buildAuthEmailRedirectUrl(nextPath = "") {
  try {
    if (typeof window === "undefined") return "";
    const origin = resolveAuthRedirectOrigin();
    if (!origin) return "";
    const base = `${origin}/auth/callback`;
    const n = String(nextPath || "").trim();
    if (n && n.startsWith("/") && !n.startsWith("//")) {
      return `${base}?next=${encodeURIComponent(n)}`;
    }
    return base;
  } catch {
    return "";
  }
}

/**
 * @param {string} provider
 * @param {{ nextPath?: string, returnTo?: string }} [opts] — `returnTo` ist Alias für `nextPath` (gleiche Semantik, ein Ziel).
 */
function __projectRefFromSupabaseUrl(url) {
  try {
    return String(
      new URL(String(url || "")).hostname.split(".")[0] || "",
    ).trim();
  } catch {
    return "";
  }
}

function __clearStalePkceVerifiers(activeProjectRef) {
  const ref = String(activeProjectRef || "").trim();
  if (!ref || typeof localStorage === "undefined") return;
  const activeKey = `sb-${ref}-auth-token-code-verifier`;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.endsWith("-code-verifier") && key !== activeKey) {
        localStorage.removeItem(key);
      }
    }
  } catch (_) {}
  try {
    if (typeof sessionStorage !== "undefined") {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.endsWith("-code-verifier")) {
          sessionStorage.removeItem(key);
        }
      }
    }
  } catch (_) {}
}

export async function signInWithOAuth(
  provider,
  { nextPath = "", returnTo = "" } = {},
) {
  resetSupabaseClientForAuthRedirect();
  const cfg = readSupabaseClientConfig();
  __clearStalePkceVerifiers(__projectRefFromSupabaseUrl(cfg.url));
  const supabase = await getSupabaseClient();
  const resolvedNext =
    String(nextPath || "").trim() || String(returnTo || "").trim();
  /** Immer explizit (localhost vs. Produktion nur über `window.location.origin`). */
  const redirectTo = buildAuthEmailRedirectUrl(resolvedNext);
  const p = String(provider || "")
    .trim()
    .toLowerCase();

  const options = {
    redirectTo:
      redirectTo ||
      `${typeof window !== "undefined" && window.location?.origin ? window.location.origin : ""}/auth/callback`,
    // For Google we force account chooser so users can switch accounts
    // after logging out from our app.
    ...(p === "google" ? { queryParams: { prompt: "select_account" } } : {}),
  };

  return await supabase.auth.signInWithOAuth({
    provider,
    options,
  });
}

export async function signInWithGoogle(options = {}) {
  return await signInWithOAuth("google", options);
}

/**
 * Startet OAuth für native Apps (Expo/React Native): liefert die Provider-URL;
 * der Aufrufer öffnet sie im In-App-Browser und tauscht den Callback per
 * {@link hydrateAuthFromOAuthCallbackUrl} ein.
 *
 * @param {string} provider
 * @param {{ redirectTo: string }} opts — z. B. `ttb://auth/callback`
 */
export async function beginNativeOAuth(provider, { redirectTo = "" } = {}) {
  const supabase = await getSupabaseClient();
  const redir = String(redirectTo || "").trim();
  if (!redir) {
    return { data: null, error: new Error("redirectTo required") };
  }
  const p = String(provider || "")
    .trim()
    .toLowerCase();
  return await supabase.auth.signInWithOAuth({
    provider: p,
    options: {
      redirectTo: redir,
      skipBrowserRedirect: true,
      ...(p === "google" ? { queryParams: { prompt: "select_account" } } : {}),
    },
  });
}

export async function beginNativeGoogleOAuth({ redirectTo = "" } = {}) {
  return await beginNativeOAuth("google", { redirectTo });
}

/**
 * Tauscht PKCE-`code` oder Hash-Tokens aus einer OAuth-Redirect-URL in eine Session.
 * Für Web: {@link hydrateAuthFromCurrentBrowserUrl}; für Mobile: Callback aus `WebBrowser`.
 *
 * @param {string} callbackUrl
 * @returns {Promise<{ ok: boolean, handled: boolean, method?: string, reason?: string, error?: unknown, cancelled?: boolean }>}
 */
export async function hydrateAuthFromOAuthCallbackUrl(callbackUrl) {
  const href = String(callbackUrl || "").trim();
  if (!href) return { ok: true, handled: false, reason: "empty-url" };

  const supabase = await getSupabaseClient();

  try {
    if (/[?&#]code=/.test(href)) {
      const exchanged = await __exchangePkceCodeForSession(
        supabase,
        href,
        "auth: native pkce exchangeCodeForSession",
      );
      if (!exchanged.ok) {
        return {
          ok: false,
          handled: true,
          method: "pkce",
          error: exchanged.error,
        };
      }
      return { ok: true, handled: true, method: "pkce" };
    }
  } catch (error) {
    return { ok: false, handled: true, reason: "pkce-parse", error };
  }

  if (/access_token=|refresh_token=/i.test(href)) {
    const hashRes = await __withAuthBootstrapTiming(
      "auth: native implicit-hash setSession",
      () => __hydrateImplicitHashSession(supabase, href),
    );
    if (hashRes.ok) return { ok: true, handled: true, method: "implicit-hash" };
    return {
      ok: false,
      handled: true,
      method: "implicit-hash",
      reason: "no-session-after-hash",
      error: hashRes.error,
    };
  }

  if (/error=/i.test(href)) {
    return {
      ok: false,
      handled: true,
      method: "oauth-error",
      reason: "error-in-url",
    };
  }

  return { ok: true, handled: false };
}

function __asStrAuth(v) {
  return String(v ?? "").trim();
}

/** PKCE-Auth-Code aus OAuth-Callback-URL (nicht die volle URL an Supabase senden). */
function __extractPkceAuthCodeFromCallbackUrl(href) {
  try {
    return __asStrAuth(new URL(String(href || "")).searchParams.get("code"));
  } catch {
    return "";
  }
}

async function __hydrateImplicitHashSession(supabase, href) {
  const raw = String(href || "").includes("#")
    ? String(href).split("#")[1] || ""
    : String(
        typeof window !== "undefined" ? window.location.hash || "" : "",
      ).replace(/^#/, "");
  const sp = new URLSearchParams(raw);
  const access_token = __asStrAuth(sp.get("access_token"));
  const refresh_token = __asStrAuth(sp.get("refresh_token"));
  if (access_token && refresh_token) {
    const type = String(sp.get("type") || "").toLowerCase();
    if (type === "recovery") {
      markPendingPasswordRecovery({ currentDocument: true });
    }
    const { data, error } = await supabase.auth.setSession({
      access_token,
      refresh_token,
    });
    if (!error && data?.session) {
      syncSupabaseAuthBearerFromSession(data.session);
      if (
        type === "recovery" ||
        sessionMatchesPendingPasswordReset(data.session)
      ) {
        markPendingPasswordRecovery({
          currentDocument: type === "recovery",
        });
      }
      return { ok: true, session: data.session };
    }
    if (error) return { ok: false, error };
  }
  const { data, error } = await ttbRunSerializedAuthGetSession(supabase);
  if (!error && data?.session) {
    syncSupabaseAuthBearerFromSession(data.session);
    return { ok: true, session: data.session };
  }
  return { ok: false, error: error || { message: "no-session-after-hash" } };
}

async function __exchangePkceCodeForSession(supabase, href, timingTag) {
  const pkceCode = __extractPkceAuthCodeFromCallbackUrl(href);
  if (!pkceCode) {
    return {
      ok: false,
      error: Object.assign(new Error("missing-pkce-code"), {
        code: "MISSING_PKCE_CODE",
      }),
    };
  }

  let recoveryDetected = false;
  let unsubscribe = null;
  const recoveryWait = new Promise((resolve) => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        recoveryDetected = true;
        markPendingPasswordRecovery({ currentDocument: true });
        resolve(true);
      }
    });
    unsubscribe = () => data.subscription.unsubscribe();
    setTimeout(() => resolve(false), 3000);
  });

  const { data, error } = await __withAuthBootstrapTiming(timingTag, () =>
    supabase.auth.exchangeCodeForSession(pkceCode),
  );
  await recoveryWait;
  if (unsubscribe) unsubscribe();

  if (error) return { ok: false, error };
  if (data?.session) {
    syncSupabaseAuthBearerFromSession(data.session);
    if (recoveryDetected || sessionMatchesPendingPasswordReset(data.session)) {
      markPendingPasswordRecovery({ currentDocument: true });
    }
  }
  return { ok: true, session: data?.session || null };
}

function __isDevAuthBootstrapLog() {
  try {
    return typeof import.meta !== "undefined" && !!import.meta.env?.DEV;
  } catch {
    return false;
  }
}

/** Fetch/Supabase bricht bei Navigation oder Strict-Mode doppelt ab — ein Retry reicht oft. */
function __isAbortLikeAuthBootstrap(err) {
  try {
    const name = String(err?.name || "").toLowerCase();
    const msg = String(err?.message || err || "").toLowerCase();
    return (
      name === "aborterror" ||
      msg.includes("operation was aborted") ||
      msg.includes("the user aborted") ||
      msg.includes("user aborted a request") ||
      msg.includes("abgebrochen")
    );
  } catch {
    return false;
  }
}

/** Nur DEV: feste Tags für Session-/Bootstrap-Diagnose (keine UI-Strings). */
function __authBootstrapDevLog(tag, extra = {}) {
  if (
    !__isDevAuthBootstrapLog() ||
    typeof console === "undefined" ||
    !console.debug
  )
    return;
  const wallMs =
    typeof performance !== "undefined" ? Math.round(performance.now()) : 0;
  console.debug(tag, { ...extra, wallMs });
}

async function __withAuthBootstrapTiming(tag, fn) {
  const t0 =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const now = () =>
    typeof performance !== "undefined" ? performance.now() : Date.now();
  try {
    const out = await fn();
    __authBootstrapDevLog(tag, {
      ms: Math.round((now() - t0) * 10) / 10,
      ok: true,
    });
    return out;
  } catch (e) {
    __authBootstrapDevLog(`${tag} (error)`, {
      ms: Math.round((now() - t0) * 10) / 10,
      ok: false,
      err: String(e?.message || e),
    });
    throw e;
  }
}

const PENDING_PASSWORD_RECOVERY_SS_KEY = "ttb-pending-password-recovery";
const PASSWORD_RESET_PENDING_LS_KEY = "ttb-password-reset-pending";
const PASSWORD_RESET_PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Session-Flag nach Recovery-Link — ohne TTL bleibt man mit Session auf /reset-password hängen. */
const PENDING_PASSWORD_RECOVERY_MAX_AGE_MS = 30 * 60 * 1000;
let __passwordRecoveryDetectedInCurrentDocument = false;

function __normalizeAuthEmail(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

/** Merkt an, dass für diese E-Mail ein Reset-Link angefordert wurde (gleicher Browser). */
export function markPasswordResetPendingForEmail(email) {
  const normalized = __normalizeAuthEmail(email);
  if (!normalized || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      PASSWORD_RESET_PENDING_LS_KEY,
      JSON.stringify({ email: normalized, at: Date.now() }),
    );
  } catch (_) {}
}

export function clearPasswordResetPendingForEmail() {
  try {
    localStorage.removeItem(PASSWORD_RESET_PENDING_LS_KEY);
  } catch (_) {}
}

export function getPasswordResetPendingForEmail() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(PASSWORD_RESET_PENDING_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = __normalizeAuthEmail(parsed?.email);
    const at = Number(parsed?.at);
    if (!email || !Number.isFinite(at)) {
      clearPasswordResetPendingForEmail();
      return null;
    }
    if (Date.now() - at > PASSWORD_RESET_PENDING_MAX_AGE_MS) {
      clearPasswordResetPendingForEmail();
      return null;
    }
    return email;
  } catch {
    return null;
  }
}

export function sessionMatchesPendingPasswordReset(session) {
  const pendingEmail = getPasswordResetPendingForEmail();
  if (!pendingEmail) return false;
  const sessionEmail = __normalizeAuthEmail(session?.user?.email);
  return Boolean(sessionEmail && sessionEmail === pendingEmail);
}

/** Recovery-Link: `type=recovery` in Query oder Hash (vor dem Strippen der OAuth-Parameter). */
export function isPasswordRecoveryInBrowserUrl() {
  if (typeof window === "undefined") return false;
  try {
    const u = new URL(window.location.href);
    if (String(u.searchParams.get("type") || "").toLowerCase() === "recovery")
      return true;
    const raw = String(window.location.hash || "").replace(/^#/, "");
    const sp = new URLSearchParams(raw);
    return String(sp.get("type") || "").toLowerCase() === "recovery";
  } catch {
    return false;
  }
}

export function markPendingPasswordRecovery({ currentDocument = false } = {}) {
  if (currentDocument) {
    __passwordRecoveryDetectedInCurrentDocument = true;
  }
  try {
    sessionStorage.setItem(
      PENDING_PASSWORD_RECOVERY_SS_KEY,
      JSON.stringify({ at: Date.now() }),
    );
  } catch (_) {}
}

export function clearPendingPasswordRecovery() {
  try {
    sessionStorage.removeItem(PENDING_PASSWORD_RECOVERY_SS_KEY);
  } catch (_) {}
}

export function hasPendingPasswordRecovery() {
  try {
    const raw = sessionStorage.getItem(PENDING_PASSWORD_RECOVERY_SS_KEY);
    if (!raw) return false;
    // Legacy-Flag ohne Timestamp: nach abgebrochenem Reset oft „ewig“ aktiv → verwerfen.
    if (raw === "1") {
      clearPendingPasswordRecovery();
      return false;
    }
    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at);
    if (!Number.isFinite(at)) {
      clearPendingPasswordRecovery();
      return false;
    }
    if (Date.now() - at > PENDING_PASSWORD_RECOVERY_MAX_AGE_MS) {
      clearPendingPasswordRecovery();
      return false;
    }
    return true;
  } catch {
    clearPendingPasswordRecovery();
    return false;
  }
}

export function capturePasswordRecoveryFromBrowserUrl() {
  if (isPasswordRecoveryInBrowserUrl()) {
    markPendingPasswordRecovery({ currentDocument: true });
  }
}

/** Recovery-Intent aus URL (type=recovery, /auth/callback?next=/reset-password). */
export function syncPasswordRecoveryIntentFromBrowserUrl() {
  capturePasswordRecoveryFromBrowserUrl();
  if (getAuthCallbackRelativeNext() === "/reset-password") {
    markPendingPasswordRecovery({ currentDocument: true });
  }
}

export function isPasswordRecoveryCallbackIntent() {
  return (
    isPasswordRecoveryInBrowserUrl() ||
    getAuthCallbackRelativeNext() === "/reset-password" ||
    hasPendingPasswordRecovery()
  );
}

function __isPasswordRecoveryFlowPath() {
  if (typeof window === "undefined") return false;
  const path =
    String(window.location?.pathname || "").replace(/\/+$/, "") || "/";
  return path === "/auth/callback" || path === "/reset-password";
}

/**
 * Recovery-Flags gelten nur im Dokument, das den Callback verarbeitet, oder
 * auf der Reset-Route selbst. Ein späteres Öffnen von `/` darf keinen alten
 * sessionStorage-Marker erneut als Passwort-Recovery interpretieren.
 */
export function clearStalePasswordRecoveryIntent(session = null) {
  if (isPasswordRecoveryInBrowserUrl()) return false;
  if (getAuthCallbackRelativeNext() === "/reset-password") return false;
  if (__isPasswordRecoveryFlowPath()) return false;
  if (__passwordRecoveryDetectedInCurrentDocument) return false;
  if (!hasPendingPasswordRecovery()) return false;
  clearPendingPasswordRecovery();
  return true;
}

/** Abbruch: Reset-Seite verlassen / zurück zur Anmeldung. */
export function abandonPasswordRecoveryFlow() {
  clearPendingPasswordRecovery();
  clearPasswordResetPendingForEmail();
}

/**
 * Nur bei echtem Recovery-Kontext erzwingen:
 * - URL enthält type=recovery oder next=/reset-password
 * - oder Session + frisches pending Recovery-Flag im aktuellen
 *   Callback-/Reset-Dokument
 *
 * Ein alleiniges sessionStorage-Flag ohne Session reicht nicht.
 * Abgelaufene Flags (TTL) erzwingen nichts mehr.
 *
 * @param {import("@supabase/supabase-js").Session|null} [session]
 */
export function shouldForcePasswordResetFlow(session = null) {
  if (isPasswordRecoveryInBrowserUrl()) return true;
  if (getAuthCallbackRelativeNext() === "/reset-password") return true;
  if (
    session &&
    hasPendingPasswordRecovery() &&
    (__passwordRecoveryDetectedInCurrentDocument ||
      __isPasswordRecoveryFlowPath())
  ) {
    return true;
  }
  return false;
}

/**
 * Pfade, auf denen `?code=` ein PKCE-/OAuth-Code sein kann.
 * Turnier-Links (`/matchmixer/anmelden?code=SLQB6D`) dürfen hier nicht landen.
 * @param {string} pathname
 */
export function isSupabasePkceCodeLandingPath(pathname) {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  return path === "/" || path === "/login" || path === "/auth/callback";
}

/**
 * Supabase Site-URL ohne `/auth/callback` → PKCE-Code landet auf `/`.
 * Normalisiert auf `/auth/callback` (Recovery: `next=/reset-password`).
 * @returns {boolean} true wenn Redirect ausgelöst wurde
 */
export function normalizePkceAuthCallbackPathInBrowser() {
  if (typeof window === "undefined") return false;
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("code")) return false;
    const path = u.pathname.replace(/\/+$/, "") || "/";
    if (path === "/auth/callback") return false;
    if (!isSupabasePkceCodeLandingPath(path)) return false;

    capturePasswordRecoveryFromBrowserUrl();
    const code = __asStrAuth(u.searchParams.get("code"));
    if (!code) return false;

    const type = String(u.searchParams.get("type") || "").toLowerCase();
    const existingNext = getAuthCallbackRelativeNext();
    const recoveryIntent =
      type === "recovery" ||
      hasPendingPasswordRecovery() ||
      Boolean(getPasswordResetPendingForEmail());
    const next = existingNext || (recoveryIntent ? "/reset-password" : "");

    const origin = resolveAuthRedirectOrigin() || u.origin;
    const target = new URL(`${origin}/auth/callback`);
    target.searchParams.set("code", code);
    if (next) target.searchParams.set("next", next);
    if (type === "recovery") target.searchParams.set("type", "recovery");
    window.location.replace(target.toString());
    return true;
  } catch {
    return false;
  }
}

let __passwordRecoveryListenerRegistered = false;

function __ensurePasswordRecoveryAuthListener() {
  if (__passwordRecoveryListenerRegistered || typeof window === "undefined")
    return;
  __passwordRecoveryListenerRegistered = true;
  subscribeSupabaseAuthStateChange((event) => {
    if (event === "PASSWORD_RECOVERY") {
      markPendingPasswordRecovery({ currentDocument: true });
    }
  });
}

__ensurePasswordRecoveryAuthListener();

/** Erkennt OAuth-Rückruf per Query (`code=`) oder Hash (`#access_token=` / `#error=`). */
export function hasSupabaseOAuthCallbackSignalsInUrl() {
  if (typeof window === "undefined") return false;
  const h = window.location.hash || "";
  if (/access_token=|refresh_token=|provider_token=|error=/i.test(h))
    return true;
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("code")) return false;
    return isSupabasePkceCodeLandingPath(u.pathname);
  } catch {
    return false;
  }
}

function __stripOAuthParamsFromBrowserUrl() {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete("code");
    u.searchParams.delete("state");
    u.searchParams.delete("scope");
    u.hash = "";
    const nextPath = `${u.pathname}${u.search}`;
    window.history.replaceState(window.history.state || {}, "", nextPath);
  } catch (_) {}
}

/**
 * Verarbeitet PKCE-`code` in der URL und/oder implizite Hash-Tokens; entfernt sensible URL-Teile danach.
 *
 * Idempotent: parallele Aufrufer (z. B. Shell-Bootstrap + AuthCallbackPage) teilen sich denselben
 * In-Flight-Promise, damit der einmalig nutzbare PKCE-Code nur einmal eingetauscht wird.
 *
 * @returns {Promise<{ ok: boolean, handled: boolean, method?: string, reason?: string, error?: unknown }>}
 */
let __hydrateInflight = null;

export async function hydrateAuthFromCurrentBrowserUrl() {
  if (
    typeof window === "undefined" ||
    !hasSupabaseOAuthCallbackSignalsInUrl()
  ) {
    __authBootstrapDevLog("auth: hydrate url", {
      skipped: true,
      reason: "no_oauth_signals",
    });
    return { ok: true, handled: false };
  }
  if (__hydrateInflight) {
    __authBootstrapDevLog("auth: hydrate url", { dedup: true });
    return __hydrateInflight;
  }
  __hydrateInflight = (async () => {
    try {
      capturePasswordRecoveryFromBrowserUrl();
      __authBootstrapDevLog("auth: hydrate url", {
        start: true,
        hasSignals: true,
      });
      const supabase = await getSupabaseClient();
      const href = window.location.href;

      try {
        const u = new URL(href);
        if (u.searchParams.has("code")) {
          const exchanged = await __exchangePkceCodeForSession(
            supabase,
            href,
            "auth: hydrate pkce exchangeCodeForSession",
          );
          __stripOAuthParamsFromBrowserUrl();
          if (!exchanged.ok) {
            return {
              ok: false,
              handled: true,
              method: "pkce",
              reason: "exchange",
              error: exchanged.error,
            };
          }
          syncPasswordRecoveryIntentFromBrowserUrl();
          if (exchanged.session) {
            return { ok: true, handled: true, method: "pkce" };
          }
          const { data, error } =
            await ttbRunSerializedAuthGetSession(supabase);
          if (error) {
            return {
              ok: false,
              handled: true,
              method: "pkce",
              reason: "getSession",
              error,
            };
          }
          if (data?.session) {
            return { ok: true, handled: true, method: "pkce" };
          }
          return {
            ok: false,
            handled: true,
            method: "pkce",
            reason: "no-session-after-code",
            error: { message: "no-session-after-pkce" },
          };
        }
      } catch (e) {
        __stripOAuthParamsFromBrowserUrl();
        return { ok: false, handled: true, reason: "pkce-parse", error: e };
      }

      const h = window.location.hash || "";
      if (/access_token=|refresh_token=/i.test(h)) {
        const hashRes = await __withAuthBootstrapTiming(
          "auth: hydrate implicit-hash setSession",
          () => __hydrateImplicitHashSession(supabase, window.location.href),
        );
        syncPasswordRecoveryIntentFromBrowserUrl();
        __stripOAuthParamsFromBrowserUrl();
        if (hashRes.ok)
          return { ok: true, handled: true, method: "implicit-hash" };
        return {
          ok: false,
          handled: true,
          method: "implicit-hash",
          reason: "no-session-after-hash",
          error: hashRes.error,
        };
      }

      if (/error=/i.test(h)) {
        __stripOAuthParamsFromBrowserUrl();
        return {
          ok: false,
          handled: true,
          method: "oauth-error-hash",
          reason: "error-in-hash",
        };
      }

      return { ok: true, handled: false };
    } finally {
      __hydrateInflight = null;
    }
  })();
  return __hydrateInflight;
}

const AUTH_BOOTSTRAP_DEFAULT_TIMEOUT_MS = 15000;

/** Verhindert parallele Bootstraps (React 18 Strict Mode, mehrere Aufrufer). */
let __bootstrapWebAuthSessionInflight = null;

/**
 * `supabase.auth.getSession()` kann bei WebKit/Strict-Mode mit **throw** `AbortError`
 * abbrechen (überlappende GoTrue-Fetches). Retries mit Backoff.
 */
async function __getSessionWithAbortRetries(
  supabase,
  { maxAttempts = 8 } = {},
) {
  let lastAbort = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await ttbRunSerializedAuthGetSession(supabase);
    } catch (e) {
      lastAbort = e;
      if (!__isAbortLikeAuthBootstrap(e)) throw e;
      __authBootstrapDevLog("auth: getSession abort (retry)", {
        attempt,
        err: String(e?.message || e),
      });
      await new Promise((r) => setTimeout(r, 50 + attempt * 80));
    }
  }
  throw lastAbort ?? new Error("getSession: abort retries exhausted");
}

async function __bootstrapWebAuthSessionInner({
  timeoutMs = AUTH_BOOTSTRAP_DEFAULT_TIMEOUT_MS,
} = {}) {
  beginAuthDebugBootstrap();
  syncPasswordRecoveryIntentFromBrowserUrl();
  const tBoot =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const now = () =>
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const run = async () => {
    setAuthDebugStep("session:start");
    const hydrateResult = await __withAuthBootstrapTiming(
      "auth: hydrate url (bootstrap)",
      () => hydrateAuthFromCurrentBrowserUrl(),
    );
    const oauthExchangeOk = Boolean(
      hydrateResult?.handled && hydrateResult?.ok,
    );
    await __withAuthBootstrapTiming(
      "auth: get supabase client (bootstrap)",
      () => getSupabaseClient(),
    );
    const supabase = await getSupabaseClient();
    const maxSessionAttempts = oauthExchangeOk ? 12 : 1;
    let sess = null;
    for (let attempt = 0; attempt < maxSessionAttempts; attempt++) {
      const tag =
        attempt === 0
          ? "auth: getSession (bootstrap)"
          : `auth: getSession (bootstrap retry ${attempt})`;
      const { data, error } = await __withAuthBootstrapTiming(tag, () =>
        __getSessionWithAbortRetries(supabase, { maxAttempts: 4 }),
      );
      if (error) {
        __authBootstrapDevLog("auth: getSession (bootstrap) returned error", {
          err: String(error.message || error),
          attempt,
        });
        setAuthDebugFailure(
          "session:getSession",
          error,
          "supabase.auth.getSession",
        );
        return null;
      }
      sess = data?.session || null;
      if (sess) {
        if (sessionMatchesPendingPasswordReset(sess)) {
          markPendingPasswordRecovery();
        }
        break;
      }
      if (oauthExchangeOk && attempt < maxSessionAttempts - 1) {
        await new Promise((r) => setTimeout(r, 80 + attempt * 120));
      }
    }
    if (sess) {
      __authBootstrapDevLog("auth: session received", { hasSession: true });
      __authBootstrapDevLog("auth: session user", {
        userId: sess?.user?.id,
        email: sess?.user?.email,
      });
    } else {
      __authBootstrapDevLog("auth: session received", {
        hasSession: false,
        oauthExchangeOk,
      });
    }
    setAuthDebugStep("session:success");
    return sess;
  };

  const timeoutErr = Object.assign(new Error("auth-bootstrap-timeout"), {
    code: "AUTH_BOOTSTRAP_TIMEOUT",
  });
  const deadline = Math.max(
    3000,
    Number(timeoutMs) || AUTH_BOOTSTRAP_DEFAULT_TIMEOUT_MS,
  );
  setAuthDebugBootstrapDeadline(deadline);

  try {
    const session = await Promise.race([
      run(),
      new Promise((_, reject) => {
        setTimeout(() => reject(timeoutErr), deadline);
      }),
    ]);
    const total = now() - tBoot;
    __authBootstrapDevLog("auth: bootstrap complete", {
      ms: Math.round(total * 10) / 10,
      hasSession: !!session,
    });
    return session;
  } catch (e) {
    const total = now() - tBoot;
    const code = e && typeof e === "object" ? e.code : "";
    if (code === "AUTH_BOOTSTRAP_TIMEOUT") {
      setAuthDebugFailure("session:timeout", e, "bootstrapWebAuthSession");
      __authBootstrapDevLog("governance: bootstrap timeout", {
        phase: "bootstrapWebAuthSession",
        ms: Math.round(total * 10) / 10,
        deadlineMs: deadline,
      });
    } else {
      setAuthDebugFailure("session:bootstrap", e, "bootstrapWebAuthSession");
      if (
        __isDevAuthBootstrapLog() &&
        typeof console !== "undefined" &&
        console.warn
      ) {
        console.warn("governance: bootstrap failed", {
          phase: "bootstrapWebAuthSession",
          err: String(e?.message || e),
        });
      }
    }
    throw e;
  }
}

/**
 * Einmalige Session-Stärkung nach Redirect: URL-Tokens verarbeiten, dann `getSession`.
 * Parallele Aufrufer (z. B. React Strict Mode) teilen **dieselbe** In-Flight-Promise.
 * @param {{ timeoutMs?: number }} [opts]
 */
export function bootstrapWebAuthSession(opts = {}) {
  if (__bootstrapWebAuthSessionInflight)
    return __bootstrapWebAuthSessionInflight;
  const p = __bootstrapWebAuthSessionInner(opts).finally(() => {
    if (__bootstrapWebAuthSessionInflight === p)
      __bootstrapWebAuthSessionInflight = null;
  });
  __bootstrapWebAuthSessionInflight = p;
  return p;
}

/**
 * OAuth-/E-Mail-Redirect: Bootstrap + Recovery, wenn die Session nach PKCE noch nicht lesbar ist.
 * @param {{ timeoutMs?: number, recoveryTimeoutMs?: number }} [opts]
 */
export async function acquireWebAuthSessionAfterRedirect(opts = {}) {
  const recoveryMs = Math.max(1500, Number(opts.recoveryTimeoutMs) || 8000);
  try {
    const session = await bootstrapWebAuthSession(opts);
    if (session) return session;
  } catch (e) {
    if (e && typeof e === "object" && e.code === "AUTH_BOOTSTRAP_TIMEOUT") {
      return await recoverWebAuthSessionAfterBootstrapTimeout({
        timeoutMs: recoveryMs,
      });
    }
    throw e;
  }
  return await recoverWebAuthSessionAfterBootstrapTimeout({
    timeoutMs: recoveryMs,
  });
}

/**
 * Nach abgelaufenem Auth-Bootstrap: kurzer zweiter Versuch über bereits erstellten Client (ohne erneutes Hydrate).
 * Liefert Session oder null — keine neuen Rechte, nur Lesen.
 * @param {{ timeoutMs?: number }} [opts]
 */
export async function recoverWebAuthSessionAfterBootstrapTimeout({
  timeoutMs = 5000,
} = {}) {
  const supa = getSupabaseClientSync();
  if (!supa?.auth) return null;
  const ms = Math.max(1500, Number(timeoutMs) || 5000);
  try {
    const result = await Promise.race([
      __getSessionWithAbortRetries(supa, { maxAttempts: 6 }).then((r) => ({
        kind: "ok",
        r,
      })),
      new Promise((resolve) =>
        setTimeout(() => resolve({ kind: "timeout" }), ms),
      ),
    ]);
    if (result?.kind === "timeout") {
      __authBootstrapDevLog("auth: session recovery timed out", {
        timeoutMs: ms,
      });
      setAuthDebugFailure(
        "session:recovery-timeout",
        { message: "recover-getsession-timeout" },
        "recoverWebAuthSession",
      );
      return null;
    }
    const { data, error } = result?.r || {};
    if (error) {
      __authBootstrapDevLog("auth: session recovery getSession error", {
        err: String(error?.message || error),
      });
      setAuthDebugFailure(
        "session:recovery-getSession",
        error,
        "recoverWebAuthSession",
      );
      return null;
    }
    const sess = data?.session || null;
    if (sess) setAuthDebugStep("session:recovery-success");
    return sess;
  } catch (e) {
    __authBootstrapDevLog("auth: session recovery failed", {
      err: String(e?.message || e),
    });
    setAuthDebugFailure("session:recovery", e, "recoverWebAuthSession");
    return null;
  }
}

/** Relatives `next` aus `/auth/callback?next=…` (und konservativ aus Hash-Query), nur interne Pfade. */
function __decodeNextParamOnce(n) {
  const s = __asStrAuth(n);
  if (!s) return "";
  if (s.startsWith("/") && !s.startsWith("//")) return s;
  try {
    const d = decodeURIComponent(s);
    if (d.startsWith("/") && !d.startsWith("//")) return d;
  } catch (_) {}
  return s;
}

export function getAuthCallbackRelativeNext() {
  if (typeof window === "undefined") return "";
  try {
    const sp = new URLSearchParams(window.location.search || "");
    let n = __decodeNextParamOnce(sp.get("next") || "");
    if (!n && window.location.hash) {
      const raw = String(window.location.hash || "").replace(/^#/, "");
      try {
        const sp2 = new URLSearchParams(raw);
        n = __decodeNextParamOnce(sp2.get("next") || "");
      } catch (_) {}
    }
    if (!n || !n.startsWith("/") || n.startsWith("//")) return "";
    return n;
  } catch {
    return "";
  }
}

/** Interne Ziele: relatives `next` mit `/`, ohne `//`-Bypass. */
export function isSafeRelativeInternalNextPath(p) {
  const s = __asStrAuth(p);
  return Boolean(s && s.startsWith("/") && !s.startsWith("//"));
}

/**
 * `next` zeigt auf eine Einladungsannahme-Route (Governance oder TrainingsDesk).
 * Konservativ: auch ohne Token = Invite-/Auth-Intent, damit `applyPendingSignupAccessRequestRemote`
 * dort nicht läuft. Fehlenden/ungültigen Token zeigt die jeweilige Einladungsseite.
 */
export function isInviteAcceptIntentNextPath(nextPath) {
  const decoded = __decodeNextParamOnce(__asStrAuth(nextPath));
  if (!isSafeRelativeInternalNextPath(decoded)) return false;
  try {
    const u = new URL(String(decoded), "https://example.invalid");
    const path = String(u.pathname || "").replace(/\/+$/, "");
    if (path === "/accept-invite") return true;
    return isTrainingsDeskInvitePathname(path);
  } catch {
    return false;
  }
}

export async function getSession() {
  const supabase = await getSupabaseClient();
  const { data, error } = await ttbRunSerializedAuthGetSession(supabase);
  if (error) return null;
  return data?.session || null;
}

export async function getUser() {
  return getSupabaseUser();
}

export async function signUpWithPassword({
  email,
  password,
  options = {},
} = {}) {
  const supabase = await getSupabaseClient();
  const res = await supabase.auth.signUp({ email, password, options });
  if (res?.data?.session) syncSupabaseAuthBearerFromSession(res.data.session);
  return res;
}

export async function signInWithPassword({ email, password } = {}) {
  const supabase = await getSupabaseClient();
  const res = await supabase.auth.signInWithPassword({ email, password });
  if (res?.data?.session) {
    syncSupabaseAuthBearerFromSession(res.data.session);
    // Normales Login ist kein Recovery-Flow — veraltete Reset-Marker verwerfen.
    clearPendingPasswordRecovery();
    clearPasswordResetPendingForEmail();
  }
  return res;
}

/**
 * Sendet die Signup-Bestätigungs-Mail an eine angegebene Adresse erneut.
 * Nutzt Supabase Auth `resend({ type: 'signup' })`. Stille Fehler werden als
 * `{ ok:false, reason }` zurückgegeben; kein Throw.
 *
 * @param {string} email
 * @param {{ nextPath?: string }} [options]
 * @returns {Promise<{ ok: boolean, reason?: string, error?: unknown }>}
 */
export async function resendSignupConfirmationEmail(email, options = {}) {
  const addr = String(email || "")
    .trim()
    .toLowerCase();
  if (!addr || !addr.includes("@")) {
    return { ok: false, reason: "invalid-email" };
  }
  try {
    const supabase = await getSupabaseClient();
    if (!supabase?.auth?.resend) return { ok: false, reason: "unsupported" };
    const emailRedirectTo = options?.nextPath
      ? buildAuthEmailRedirectUrl(String(options.nextPath || ""))
      : undefined;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: addr,
      ...(emailRedirectTo ? { options: { emailRedirectTo } } : {}),
    });
    if (error) return { ok: false, reason: "supabase-error", error };
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "exception", error };
  }
}

export function onAuthStateChange(cb) {
  const unsubscribe = subscribeSupabaseAuthStateChange(cb);
  return { data: { subscription: { unsubscribe } } };
}

export async function signOut() {
  await Promise.allSettled([
    import("../hub/hub-scoped-cache.js").then((m) =>
      m.clearRegisteredHubCaches?.(),
    ),
    import("../trainer/drills.js").then((m) => m.clearGlobalDrillsCache?.()),
  ]);
  const supabase = await getSupabaseClient();
  return await supabase.auth.signOut();
}

/**
 * Redirect-Ziel für Supabase-Passwort-Recovery (E-Mail-Link).
 * Über `/auth/callback?next=/reset-password` — gleiche Allowlist wie OAuth/Signup.
 */
export function buildPasswordResetRedirectUrl() {
  return buildAuthEmailRedirectUrl("/reset-password");
}

/**
 * Passwort-Reset-Link per E-Mail (Supabase Auth).
 * @param {string} email
 * @param {string} [redirectTo]
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function requestPasswordResetEmail(email, redirectTo) {
  const trimmed = String(email || "")
    .trim()
    .toLowerCase();
  if (!trimmed) {
    return { ok: false, reason: "missing-email" };
  }

  const supabase = await getSupabaseClient();
  if (!supabase) {
    return { ok: false, reason: "no-supabase" };
  }

  const redir =
    String(redirectTo || "").trim() || buildPasswordResetRedirectUrl();
  markPasswordResetPendingForEmail(trimmed);
  const options = redir ? { redirectTo: redir } : {};
  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, options);
  if (error) {
    return { ok: false, reason: error.message || "reset-failed" };
  }

  return { ok: true };
}

/**
 * Neues Passwort setzen (Recovery-Session aus E-Mail-Link oder eingeloggt).
 * @param {string} newPassword
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function updateAuthUserPassword(newPassword) {
  const password = String(newPassword || "").trim();
  if (password.length < 8) {
    return { ok: false, reason: "password-too-short" };
  }

  const supabase = await getSupabaseClient();
  if (!supabase) {
    return { ok: false, reason: "no-supabase" };
  }

  let session = null;
  try {
    const { data } = await supabase.auth.getSession();
    session = data?.session ?? null;
  } catch {
    return { ok: false, reason: "session-error" };
  }

  if (!session) {
    return { ok: false, reason: "not-authenticated" };
  }

  const existingMeta =
    session.user?.user_metadata &&
    typeof session.user.user_metadata === "object"
      ? session.user.user_metadata
      : {};
  const { error } = await supabase.auth.updateUser({
    password,
    data: { ...existingMeta, must_set_password: false },
  });
  if (error) {
    return { ok: false, reason: error.message || "update-failed" };
  }

  clearPendingPasswordRecovery();
  clearPasswordResetPendingForEmail();

  try {
    await supabase.auth.signOut();
  } catch (_) {
    /* noop */
  }

  return { ok: true };
}

/**
 * Passwort ändern (eingeloggt) — Session bleibt aktiv.
 * @param {string} newPassword
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function changeAuthenticatedUserPassword(newPassword) {
  const password = String(newPassword || "").trim();
  if (password.length < 8) {
    return { ok: false, reason: "password-too-short" };
  }

  const supabase = await getSupabaseClient();
  if (!supabase) {
    return { ok: false, reason: "no-supabase" };
  }

  let session = null;
  try {
    const { data } = await supabase.auth.getSession();
    session = data?.session ?? null;
  } catch {
    return { ok: false, reason: "session-error" };
  }

  if (!session) {
    return { ok: false, reason: "not-authenticated" };
  }

  const { error } = await supabase.auth.updateUser({ password });
  if (error) {
    return { ok: false, reason: error.message || "update-failed" };
  }

  return { ok: true };
}
